import { useState, useCallback, useRef, useEffect } from 'preact/hooks';
import { BaseDialog, BtnCancel } from '@components/dialog';
import { BtnPrimary } from '@components/buttons';
import { useIntl } from 'shared/intl/use-intl';
import { IcoCrop, IcoRotate } from '@components/icons';
import { ImageCropper, applyCrop } from './image-cropper';
import { CropArea } from './crop-area';
import { showError } from '@components/app-error';

type ImageEditorProps = {
  file: File;
  previewUrl: string;
  aspectRatio?: number;
  requireCrop?: boolean;
  confirmText?: string;
  onCancel: () => void;
  onConfirm: (editedFile?: File) => void;
};

function ImageDisplay({
  previewUrl,
  rotation,
  onLoad,
  onError,
}: {
  previewUrl: string;
  rotation: number;
  onLoad: (image: HTMLImageElement) => void;
  onError: () => void;
}) {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const [containerSize, setContainerSize] = useState<{ width: number; height: number } | null>(
    null,
  );
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const updateContainerSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };

    updateContainerSize();
    window.addEventListener('resize', updateContainerSize);
    return () => window.removeEventListener('resize', updateContainerSize);
  }, []);

  useEffect(() => {
    const img = imageRef.current;
    if (!img) {
      return;
    }

    const handleLoad = () => {
      if (!img.naturalWidth) {
        onError();
        return;
      }
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
      onLoad(img);
    };

    img.addEventListener('load', handleLoad);
    img.addEventListener('error', onError);
    if (img.complete) {
      handleLoad();
    }

    return () => {
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', onError);
    };
  }, [onLoad, onError]);

  const getImageDisplaySize = () => {
    if (!imageSize || !containerSize) {
      return { width: '100%', height: '100%' };
    }

    const { width: imgW, height: imgH } = imageSize;
    const { width: containerW, height: containerH } = containerSize;

    const isRotated = rotation % 180 !== 0;
    const displayW = isRotated ? imgH : imgW;
    const displayH = isRotated ? imgW : imgH;

    const scaleX = containerW / displayW;
    const scaleY = containerH / displayH;
    const scale = Math.min(scaleX, scaleY, 1);

    return {
      width: `${displayW * scale}px`,
      height: `${displayH * scale}px`,
    };
  };

  const displaySize = getImageDisplaySize();

  return (
    <div ref={containerRef} class="w-full h-full flex items-center justify-center">
      <img
        ref={imageRef}
        src={previewUrl}
        style={{
          objectFit: 'contain',
          width: displaySize.width,
          height: displaySize.height,
          transform: `rotate(${rotation}deg)`,
        }}
        alt="Image preview"
      />
    </div>
  );
}

type ImageEditorState = {
  isLoading: boolean;
  rotation: number;
  imageEl: HTMLImageElement | null;
  isCropMode: boolean;
  cropArea: CropArea | null;
  cropKey: number;
};

export function ImageEditor({
  file,
  previewUrl,
  aspectRatio,
  requireCrop,
  confirmText,
  onCancel,
  onConfirm,
}: ImageEditorProps) {
  const intl = useIntl();
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [editorState, setEditorState] = useState<ImageEditorState>({
    isLoading: true,
    rotation: 0,
    imageEl: null,
    isCropMode: false,
    cropArea: null,
    cropKey: 0,
  });
  const handleImageLoad = useCallback(
    (loadedImage: HTMLImageElement) => {
      setEditorState((s) => ({ ...s, imageEl: loadedImage, isLoading: false }));
      if (requireCrop) {
        requestAnimationFrame(() => setEditorState((s) => ({ ...s, isCropMode: true })));
      }
    },
    [requireCrop],
  );

  const handleImageError = useCallback(() => {
    if (requireCrop) {
      showError(new Error("This image can't be opened for cropping. Try a JPG or PNG."));
      onCancel();
      return;
    }
    onConfirm(file);
  }, [requireCrop, file, onCancel, onConfirm]);

  const handleRotate = useCallback(() => {
    setEditorState((s) => ({ ...s, rotation: (s.rotation + 90) % 360 }));
    requestAnimationFrame(() => setEditorState((s) => ({ ...s, cropKey: s.cropKey + 1 })));
  }, []);

  const handleToggleCrop = useCallback(() => {
    setEditorState((s) => ({ ...s, isCropMode: !s.isCropMode }));
  }, []);

  const handleCropChange = useCallback((newCropArea: CropArea) => {
    setEditorState((s) => ({ ...s, cropArea: newCropArea }));
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!editorState.imageEl) {
      return onConfirm();
    }
    if (!editorState.isCropMode && editorState.rotation === 0) {
      return onConfirm(file);
    }

    try {
      let finalBlob: Blob;
      if (editorState.isCropMode && editorState.cropArea && imageContainerRef.current) {
        finalBlob = await applyCrop(
          editorState.imageEl,
          editorState.cropArea,
          imageContainerRef.current,
          editorState.rotation,
          file.type,
        );
      } else {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        const { naturalWidth: w, naturalHeight: h } = editorState.imageEl;
        if (editorState.rotation % 180 === 0) {
          canvas.width = w;
          canvas.height = h;
        } else {
          canvas.width = h;
          canvas.height = w;
        }
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((editorState.rotation * Math.PI) / 180);
        ctx.drawImage(editorState.imageEl, -w / 2, -h / 2);
        finalBlob = await new Promise((res, rej) =>
          canvas.toBlob(
            (b) => (b ? res(b) : rej(new Error('Canvas toBlob failed'))),
            file.type,
            0.92,
          ),
        );
      }
      onConfirm(new File([finalBlob], file.name, { type: file.type }));
    } catch (error) {
      showError(error);
    }
  }, [editorState, file, onConfirm]);

  return (
    <BaseDialog onClose={onCancel} blurBg={false} contentWidth={true} zIndex="z-[70]">
      <div class="grid grid-rows-[auto_1fr_auto] bg-white rounded-lg shadow-xl w-[min(64rem,calc(100vw-2rem))] h-[min(48rem,calc(100vh-4rem))] overflow-hidden">
        <header class="p-6 pb-4 border-b border-gray-200 flex-shrink-0">
          <h2 class="font-semibold text-xl text-gray-900">{intl('Edit Image')}</h2>
          <p class="text-gray-500 text-sm mt-1 truncate">{file.name}</p>
        </header>
        <div class="p-6 min-h-0 relative">
          <div
            ref={imageContainerRef}
            class="w-full h-full flex items-center justify-center bg-gray-50 rounded-lg relative overflow-hidden"
          >
            {editorState.isLoading && (
              <div class="absolute inset-0 flex items-center justify-center text-gray-500 bg-gray-50 z-10">
                <div class="text-center">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  {intl('Loading...')}
                </div>
              </div>
            )}
            <ImageDisplay
              previewUrl={previewUrl}
              rotation={editorState.rotation}
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            {editorState.isCropMode && editorState.imageEl && imageContainerRef.current && (
              <ImageCropper
                key={editorState.cropKey}
                aspectRatio={aspectRatio}
                imageElement={editorState.imageEl}
                containerElement={imageContainerRef.current}
                onCropChange={handleCropChange}
              />
            )}
          </div>
        </div>

        <footer class="p-4 md:p-6 border-t flex flex-col md:flex-row items-center gap-3">
          {!requireCrop && (
            <button
              type="button"
              disabled={editorState.isLoading}
              class={`w-full md:w-auto justify-center px-4 py-2 rounded-md border flex items-center gap-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed order-2 md:order-1 ${
                editorState.isCropMode
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              onClick={handleToggleCrop}
            >
              <IcoCrop class="size-4" />
              {intl('Crop')}
            </button>
          )}
          <button
            type="button"
            disabled={editorState.isLoading}
            class="w-full md:w-auto justify-center px-4 py-2 rounded-md border bg-white border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed order-3 md:order-2"
            onClick={handleRotate}
          >
            <IcoRotate class="size-4" />
            {intl('Rotate')}
          </button>
          <BtnCancel
            class="w-full md:w-auto justify-center px-4 py-2 rounded-md border bg-white border-gray-300 text-gray-700 order-last md:order-3 md:ml-auto"
            onClick={onCancel}
          >
            {intl('Cancel')}
          </BtnCancel>
          <BtnPrimary
            class="rounded-md w-full md:w-auto justify-center order-1 md:order-4"
            disabled={editorState.isLoading || (requireCrop && !editorState.cropArea)}
            onClick={handleConfirm}
          >
            {confirmText || intl('Apply Changes')}
          </BtnPrimary>
        </footer>
      </div>
    </BaseDialog>
  );
}
